export type DiffSegment = {
  type: "add" | "remove" | "context";
  text: string;
};

function lcsDiff<T>(
  a: T[],
  b: T[],
  eq: (x: T, y: T) => boolean,
): { type: "add" | "remove" | "context"; value: T }[] {
  const m = a.length;
  const n = b.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () =>
    Array(n + 1).fill(0),
  );
  for (let i = m - 1; i >= 0; i--) {
    for (let j = n - 1; j >= 0; j--) {
      if (eq(a[i], b[j])) dp[i][j] = dp[i + 1][j + 1] + 1;
      else dp[i][j] = Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const out: { type: "add" | "remove" | "context"; value: T }[] = [];
  let i = 0;
  let j = 0;
  while (i < m && j < n) {
    if (eq(a[i], b[j])) {
      out.push({ type: "context", value: a[i] });
      i++;
      j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      out.push({ type: "remove", value: a[i] });
      i++;
    } else {
      out.push({ type: "add", value: b[j] });
      j++;
    }
  }
  while (i < m) {
    out.push({ type: "remove", value: a[i++] });
  }
  while (j < n) {
    out.push({ type: "add", value: b[j++] });
  }
  return out;
}

export function diffLines(oldStr: string, newStr: string): DiffSegment[] {
  const oldLines = oldStr.split("\n");
  const newLines = newStr.split("\n");
  return lcsDiff(oldLines, newLines, (a, b) => a === b).map((s) => ({
    type: s.type,
    text: s.value,
  }));
}

export function diffWords(oldStr: string, newStr: string): DiffSegment[] {
  const tokenize = (s: string) => s.split(/(\s+)/).filter((t) => t.length > 0);
  const oldTokens = tokenize(oldStr);
  const newTokens = tokenize(newStr);
  return lcsDiff(oldTokens, newTokens, (a, b) => a === b).map((s) => ({
    type: s.type,
    text: s.value,
  }));
}

export function isMultiline(s: string): boolean {
  return s.includes("\n") || s.length > 120;
}
